import {
  ConditionType,
  ConditionTypeList,
} from "@intelligems/ig-types/dtos/shipping-rate-conditions";

/** Shopify's weight unit enum mapped to the store units Intelligems rates use. */
const SHOPIFY_WEIGHT_UNITS: Record<string, ConditionType> = {
  GRAMS: "g",
  KILOGRAMS: "kg",
  OUNCES: "oz",
  POUNDS: "lb",
};

const GRAMS_PER_UNIT: Record<ConditionType, number> = {
  g: 1,
  kg: 1000,
  oz: 28.349523125,
  lb: 453.59237,
};

const isStoreWeightUnit = (unit: string): unit is ConditionType =>
  (ConditionTypeList as readonly string[]).includes(unit);

/** The store unit for a Shopify enum or store unit; null when neither. */
export const storeWeightUnit = (unit: string): ConditionType | null =>
  SHOPIFY_WEIGHT_UNITS[unit] ?? (isStoreWeightUnit(unit) ? unit : null);

/** Short label for a weight unit ("KILOGRAMS" and "kg" both read "kg"). */
export const weightUnitLabel = (unit: string): string =>
  storeWeightUnit(unit) ?? unit.toLowerCase();

/**
 * Converts a weight between units, either side given as a Shopify enum or a
 * store unit. A unit outside both vocabularies cannot be converted, so the
 * value is returned as is rather than guessed.
 */
export const convertWeight = (
  value: number,
  from: string,
  to: string
): number => {
  const fromUnit = storeWeightUnit(from);
  const toUnit = storeWeightUnit(to);
  if (!fromUnit || !toUnit || fromUnit === toUnit) {
    return value;
  }
  return (value * GRAMS_PER_UNIT[fromUnit]) / GRAMS_PER_UNIT[toUnit];
};
